import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Euro, TrendingUp, Calendar, FileText, Clock, CreditCard } from "lucide-react";
import { StudentBilling } from "@/hooks/useFinanceData";

interface FinancesMetricsProps {
  billing: StudentBilling[];
}

export const FinancesMetrics = ({ billing }: FinancesMetricsProps) => {
  const totalPending = billing.filter(b => b.status === 'pending').reduce((sum, b) => sum + b.total_amount, 0);
  const totalPaid = billing.filter(b => b.status === 'paid').reduce((sum, b) => sum + b.total_amount, 0);
  const totalOverdue = billing.filter(b => b.status === 'overdue').reduce((sum, b) => sum + b.total_amount, 0);
  const totalRevenue = totalPaid + totalPending;

  const pendingInvoices = billing.filter(b => b.status === 'pending').length;
  const overdueInvoices = billing.filter(b => b.status === 'overdue').length;

  // Métricas por tipo de facturación
  const perSessionBills = billing.filter(b => b.billing_type === 'per_session');
  const monthlyBills = billing.filter(b => b.billing_type === 'monthly');
  
  const perSessionRevenue = perSessionBills.reduce((sum, b) => sum + b.total_amount, 0);
  const monthlyRevenue = monthlyBills.reduce((sum, b) => sum + b.total_amount, 0);

  return (
    <div className="space-y-6">
      {/* Métricas principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="bg-gradient-card shadow-card hover:shadow-lg transition-smooth">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Ingresos Totales</CardTitle>
            <Euro className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-success">
              {totalRevenue.toFixed(2)}€
            </div>
            <p className="text-xs text-muted-foreground">
              {billing.length} facturas generadas
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-card shadow-card hover:shadow-lg transition-smooth">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Cobrado</CardTitle>
            <TrendingUp className="h-4 w-4 text-success" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-success">
              {totalPaid.toFixed(2)}€
            </div>
            <p className="text-xs text-success">
              {((totalPaid / totalRevenue) * 100 || 0).toFixed(1)}% del total
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-card shadow-card hover:shadow-lg transition-smooth">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pendiente</CardTitle>
            <Calendar className="h-4 w-4 text-warning" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-warning">
              {totalPending.toFixed(2)}€
            </div>
            <p className="text-xs text-muted-foreground">
              {pendingInvoices} facturas pendientes
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-card shadow-card hover:shadow-lg transition-smooth">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Vencido</CardTitle>
            <FileText className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">
              {totalOverdue.toFixed(2)}€
            </div>
            <p className="text-xs text-destructive">
              {overdueInvoices} facturas vencidas
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Métricas por tipo de facturación */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="bg-gradient-card shadow-card hover:shadow-lg transition-smooth">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              Por Día
              <Badge variant="outline" className="text-xs">
                <Clock className="w-3 h-3 mr-1" />
                {perSessionBills.length}
              </Badge>
            </CardTitle>
            <Clock className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {perSessionRevenue.toFixed(2)}€
            </div>
            <p className="text-xs text-muted-foreground">
              {((perSessionRevenue / totalRevenue) * 100 || 0).toFixed(1)}% del total
            </p>
            <div className="mt-2 flex gap-2">
              <Badge variant="secondary" className="text-xs">
                Promedio: {perSessionBills.length > 0 ? (perSessionRevenue / perSessionBills.length).toFixed(2) : '0.00'}€
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-card shadow-card hover:shadow-lg transition-smooth">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              Mensual
              <Badge variant="outline" className="text-xs">
                <CreditCard className="w-3 h-3 mr-1" />
                {monthlyBills.length}
              </Badge>
            </CardTitle>
            <CreditCard className="h-4 w-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">
              {monthlyRevenue.toFixed(2)}€
            </div>
            <p className="text-xs text-muted-foreground">
              {((monthlyRevenue / totalRevenue) * 100 || 0).toFixed(1)}% del total
            </p>
            <div className="mt-2 flex gap-2">
              <Badge variant="secondary" className="text-xs">
                Promedio: {monthlyBills.length > 0 ? (monthlyRevenue / monthlyBills.length).toFixed(2) : '0.00'}€
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>

  );
};